import { z } from "zod";

import { JobsScrapeStatus } from "@/enum/scrape-enum";
import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { ListStatus } from "@/enum/list.enum";

import { CsvEnrichmentOrganizationSchema } from "./csvEnrichmentOrganization";
import { CsvEnrichmentPersonSchema } from "./csvEnrichmentPerson";

const { REQUIRED } = ERROR_TYPE;

export const CsvEnrichmentSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	content: z.string().optional(),
	status: z.nativeEnum(JobsScrapeStatus),
	people: z.array(CsvEnrichmentPersonSchema),
	organizations: z.array(CsvEnrichmentOrganizationSchema),
	allCount: z.number().nullable(),
	doneCount: z.number().nullable(),
	processedCount: z.number().nullable(),
	amountInCsv: z.number(),
	// Rows of this file the list already holds — computed when the mapping is saved (PREC-3091).
	duplicateCount: z.number().optional().nullable(),
	type: z.nativeEnum(CsvEnrichmentType),
	activeStatus: z.nativeEnum(ListStatus),
	isImported: z.boolean(),
	configuration: z
		.array(
			z.object({
				foundField: z.string(),
				assignedField: z.string(),
			})
		)
		.or(z.string())
		.optional()
		.nullable(),
	originalCsvEnrichmentId: z.number().optional().nullable(),
	altered: z.boolean(),
	originalCsvEnrichment: z
		.object({
			id: z.number(),
			name: z.string(),
			amountInCsv: z.number(),
		})
		.optional()
		.nullable(),
	webhook: z.boolean(),
	screenTitles: z.boolean().optional(),
	lists: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
			})
		)
		.optional(),
});

export const CreateCsvEnrichmentSchema = z.object({
	id: z.number().optional(),
	name: z.string().optional(),
	file: z.string().optional(),
	webhook: z.boolean(),
	listId: z.number().optional(),
	screenTitles: z.boolean().optional(),
});

export const CsvEnrichmentResponseSchema = z.object({
	data: z.array(CsvEnrichmentSchema),
	meta: MetaDataSchema,
});

export const SaveMappingResponseSchema = z.object({
	csvEnrichment: CsvEnrichmentSchema.partial(),
	// The list already finished, so the file just mapped waits until the list is picked back up.
	requiresListRestart: z.boolean(),
});

export const SuggestCsvMappingRequestSchema = z.object({
	entityType: z.nativeEnum(CsvEnrichmentType),
	headers: z.array(z.string()),
	sampleRows: z.array(z.array(z.string())),
});

export const SuggestCsvMappingResponseSchema = z.object({
	mapping: z.array(
		z.object({
			foundField: z.string(),
			assignedField: z.string(),
		})
	),
});

export type CsvEnrichment = z.infer<typeof CsvEnrichmentSchema>;

export type CreateCsvEnrichment = z.infer<typeof CreateCsvEnrichmentSchema>;

export type CsvEnrichmentResponse = z.infer<typeof CsvEnrichmentResponseSchema>;

export type SaveMappingResponse = z.infer<typeof SaveMappingResponseSchema>;

export type SuggestCsvMappingRequest = z.infer<typeof SuggestCsvMappingRequestSchema>;

export type SuggestCsvMappingResponse = z.infer<typeof SuggestCsvMappingResponseSchema>;
